import React from 'react';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { Check } from '@material-ui/icons';
import cx from 'classnames';
import { useSmartEmailContext } from '../../context/smart-email-context';
import AutoFix from '../../images/auto_fix.svg';
import UndoIcon from '../../images/undo.svg';
import { getSmartEmailConfig } from '../../lib/api/envelope/envelope-api';
import { useIsSmallScreenMediaQuery } from '../../lib/isSmallScreen';

type Props = {
  envelopeId: string;
  emailBody: string;
  setEmailBody: (body: string) => void;
};

const tones = ['Professional', 'Friendly', 'Urgent'];

export function SmartEmail(props: Props) {
  const { tone: activeTone, setTone, loading, setLoading, originalBody, setOriginalBody } = useSmartEmailContext();
  const mobile = useIsSmallScreenMediaQuery();

  async function changeTone(t: string) {
    setTone(t);
    setLoading(true);
    // Always save the current body before applying a new tone
    setOriginalBody(props.emailBody);
    const response = await getSmartEmailConfig({
      tone: t.toLowerCase(),
      body: props.emailBody,
      envelopeId: props.envelopeId,
    });
    props.setEmailBody(response);
    setLoading(false);
  }

  function handleUndo() {
    props.setEmailBody(originalBody);
    setOriginalBody('');
    setTone('');
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <InputLabel htmlFor="email-body">Email Message</InputLabel>
        {originalBody && (
          <Button onClick={handleUndo} startIcon={<img src={UndoIcon} alt="" />}>
            Undo
          </Button>
        )}
      </div>
      <div className="bg-gradient-to-r from-purple-500 to-blue-400 px-4 py-2 flex gap-3 items-center max-sm:fixed max-sm:bottom-[71px] left-0 right-0 z-10">
        <div className="text-white flex flex-col md:flex-row md:gap-2 md:items-center">
          <div className="flex items-center gap-3">
            <img src={AutoFix} alt="" />
            <b className="sm:whitespace-nowrap">{mobile ? 'Enhance to sound' : 'Enhance my email to sound:'}</b>
          </div>
        </div>
        {!mobile &&
          tones.map((tone) => (
            <Button
              key={tone}
              id={tone}
              className={cx(
                'bg-white bg-opacity-25 text-white hover:bg-opacity-50 px-3 text-[14px] py-1',
                !loading && activeTone === tone && 'bg-opacity-100 text-black',
                loading && activeTone !== tone && 'opacity-50'
              )}
              onClick={() => changeTone(tone)}
              disabled={loading}
              startIcon={
                <div className="relative">
                  {loading && activeTone === tone && (
                    <CircularProgress
                      size={18}
                      className="absolute inset-0 m-0 p-0 text-white -left-[1px] -top-[1px]"
                    />
                  )}
                  <Check
                    className={cx(
                      'mr-1 rounded-full border border-solid text-[12px] p-0.5',
                      loading && 'border-0',
                      !loading && activeTone === tone ? 'bg-blue-800 border-transparent text-white' : 'border-white'
                    )}
                  />
                </div>
              }
            >
              {tone}
            </Button>
          ))}
        {mobile && (
          <div className="flex justify-end w-full">
            <Select
              value={activeTone}
              fullWidth
              onChange={(e) => changeTone(e.target.value as string)}
              className="max-w-[250px]"
              classes={{
                root: cx('bg-white border-none flex', !activeTone && 'bg-opacity-25'),
                selectMenu: cx('rounded-full', !activeTone && 'text-white'),
                icon: cx(!activeTone && 'text-white'),
              }}
            >
              {tones.map((t) => {
                return (
                  <MenuItem key={t} value={t}>
                    {activeTone === t && (
                      <Check className="mr-2 rounded-full border border-solid text-[12px] p-0.5 bg-blue-800 border-transparent text-white relative top-[2px]" />
                    )}
                    {t}
                  </MenuItem>
                );
              })}
            </Select>
          </div>
        )}
      </div>
    </>
  );
}
